import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ForeplayAd } from '@/types'

export interface ReferenceAdCardProps {
  ad: ForeplayAd
  isSelected: boolean
  onSelect: (adId: string) => void
  /** Score to display (overrides ad.overall_score if provided) */
  score?: number
  /** Disable selection (e.g., during scoring) */
  disabled?: boolean
  /** Click handler for image preview */
  onPreview?: (ad: ForeplayAd) => void
  /** Visual variant - affects accent colors */
  variant?: 'product' | 'competitor'
}

export function ReferenceAdCard({
  ad,
  isSelected,
  onSelect,
  score,
  disabled = false,
  onPreview,
  variant = 'product',
}: ReferenceAdCardProps) {
  // Determine which score to display
  const displayScore = score ?? ad.overall_score

  // Colors based on variant
  const colors = {
    product: {
      borderSelected: 'border-blue-500 ring-2 ring-blue-200',
      borderUnselected: 'border-gray-200 hover:border-gray-300',
      checkBg: 'bg-blue-500',
      brandBg: 'bg-black/50',
    },
    competitor: {
      borderSelected: 'border-orange-500 ring-2 ring-orange-200',
      borderUnselected: 'border-gray-200 hover:border-gray-300',
      checkBg: 'bg-orange-500',
      brandBg: 'bg-orange-500/80',
    },
  }

  const c = colors[variant]
  const imageUrl = ad.url || ad.download_s3_url || 'https://via.placeholder.com/200'

  const handleCardClick = () => {
    if (disabled) return
    if (onPreview) {
      onPreview(ad)
    } else {
      onSelect(ad.id)
    }
  }

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return
    onSelect(ad.id)
  }

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        'relative rounded-lg overflow-hidden border-2 transition-all',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md',
        isSelected ? c.borderSelected : c.borderUnselected
      )}
    >
      {/* Image */}
      <div className={cn('aspect-square', onPreview && 'cursor-zoom-in')}>
        <img
          src={imageUrl}
          alt={ad.name || 'Reference ad'}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200?text=No+Image'
          }}
        />
      </div>

      {/* Score badge (bottom left) */}
      {displayScore != null && (
        <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs font-medium px-1.5 py-0.5 rounded">
          {Math.round(displayScore)}
        </div>
      )}

      {/* Select checkbox (top right) */}
      <div
        onClick={handleCheckboxClick}
        className={cn(
          'absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center transition-colors',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          isSelected ? c.checkBg : 'bg-white/80 hover:bg-white border border-gray-300'
        )}
      >
        {isSelected && <Check className="w-4 h-4 text-white" />}
      </div>

      {/* Brand name (bottom) - only show if no score displayed */}
      {ad.brand_name && displayScore == null && (
        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 text-white text-xs p-1 truncate',
            c.brandBg
          )}
        >
          {ad.brand_name}
        </div>
      )}
    </div>
  )
}
